/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Product-ID Configuration Service (P-1)
 *
 * The frontend bakes in its own copy of the QLM<->Stripe product maps
 * (lib/constants.ts, config/product-ids.ts). Those must stay in lockstep with
 * the backend constants, and the TSL product-ID bug class came partly from that
 * copy drifting. This service makes the backend the single source of truth: it
 * loads the canonical maps once at startup from
 *   GET /api/v1/config/product-ids/   (apps.core.views.get_product_id_config)
 * and merges them over the baked-in constants.
 *
 * The baked-in constants remain the synchronous static fallback. Every getter
 * returns a valid value at all times:
 *   - before the fetch resolves           -> baked-in constants (today's behaviour)
 *   - if the fetch fails                   -> baked-in constants
 *   - if the payload version is incompatible -> baked-in constants
 *   - after a successful, compatible load  -> backend values merged over baked-in
 *
 * Merge-over (not replace) is deliberate: the backend map is the authority for
 * shared keys, while keys that exist only on the frontend (e.g. the "tsl"/"dt"
 * aliases in stripeProductMapping) are preserved. Server values for shared keys
 * always win.
 *
 * @module services/productIdConfig
 */

import { api } from "@/lib/http-client";
import { logger } from "@/lib/logger";
import {
  stripeProductMapping,
  STRIPE_TO_QLM_PRODUCT_MAP,
  TSL_FEATURES_TO_PRODUCT_MAP,
  TSL_PAID_PRODUCTS,
} from "@/lib/constants";
import { QLM_PRODUCT_ID_TSL, QLM_PRODUCT_ID_TSL_TRIAL } from "@/config/product-ids";

// ============================================================================
// Types
// ============================================================================

/**
 * Response shape of GET /api/v1/config/product-ids/.
 * Mirrors backend apps.core.product_id_config.build_product_id_config().
 * QLM ids arrive as strings (JSON), so stripe_to_qlm is coerced to number below.
 */
interface ProductIdConfigPayload {
  version: number;
  qlm_to_stripe: Record<string, string>;
  stripe_to_qlm: Record<string, string | number>;
  tsl_features_to_stripe: Record<string, string>;
  tsl_paid_products: string[];
  tsl_product_id: string;
  tsl_trial_product_id: string;
}

interface ProductIdRuntime {
  qlmToStripe: Record<string, string>;
  stripeToQlm: Record<string, number>;
  tslFeaturesToStripe: Record<string, string>;
  tslPaidProducts: Set<string>;
  tslProductId: string;
  tslTrialProductId: string;
}

// ============================================================================
// Static fallback + live runtime
// ============================================================================

/**
 * The payload version this client understands. If the backend sends a different
 * version the payload is ignored and the baked-in constants remain in effect.
 */
export const SUPPORTED_PRODUCT_ID_CONFIG_VERSION = 1;

/**
 * Build the static-fallback runtime from the baked-in constants. Returns fresh
 * copies so a later load (or a test reset) never mutates the imported constants.
 */
function buildStaticRuntime(): ProductIdRuntime {
  return {
    qlmToStripe: { ...stripeProductMapping },
    stripeToQlm: { ...STRIPE_TO_QLM_PRODUCT_MAP },
    tslFeaturesToStripe: { ...TSL_FEATURES_TO_PRODUCT_MAP },
    tslPaidProducts: new Set<string>(TSL_PAID_PRODUCTS),
    tslProductId: QLM_PRODUCT_ID_TSL,
    tslTrialProductId: QLM_PRODUCT_ID_TSL_TRIAL,
  };
}

/** Live configuration. Initialised to the static fallback (safe synchronous default). */
let runtime: ProductIdRuntime = buildStaticRuntime();

/** Single-flight guard so startup + any caller share one in-flight request. */
let loadPromise: Promise<void> | null = null;

/** True once a compatible backend payload has been applied. */
let loaded = false;

// ============================================================================
// Getters (consumers call these per-use so they always see current values)
// ============================================================================

/** QLM product id -> Stripe product id (mirrors `stripeProductMapping`). */
export function getQlmToStripe(): Record<string, string> {
  return runtime.qlmToStripe;
}

/** Stripe product id -> QLM product id (mirrors `STRIPE_TO_QLM_PRODUCT_MAP`). */
export function getStripeToQlm(): Record<string, number> {
  return runtime.stripeToQlm;
}

/** QLM Features value -> Stripe product id (mirrors `TSL_FEATURES_TO_PRODUCT_MAP`). */
export function getTslFeaturesToStripe(): Record<string, string> {
  return runtime.tslFeaturesToStripe;
}

/** Set of paid TSL Stripe product ids (mirrors `TSL_PAID_PRODUCTS`). */
export function getTslPaidProducts(): Set<string> {
  return runtime.tslPaidProducts;
}

/** Canonical QLM product id for Text Speaker Live (single-ID, "22"). */
export function getTslProductId(): string {
  return runtime.tslProductId;
}

/** QLM product id for the Text Speaker Live trial. */
export function getTslTrialProductId(): string {
  return runtime.tslTrialProductId;
}

/** Whether the backend configuration has been loaded and applied. */
export function isProductIdConfigLoaded(): boolean {
  return loaded;
}

// ============================================================================
// Loading
// ============================================================================

/**
 * Coerce a map whose values may be strings or numbers into a number map,
 * dropping any entry that is not a finite number. Backend QLM ids are strings
 * in JSON; the frontend `STRIPE_TO_QLM_PRODUCT_MAP` type is Record<string, number>.
 */
function toNumberMap(src: Record<string, string | number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(src)) {
    const n = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(n)) {
      out[key] = n;
    }
  }
  return out;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Apply a validated payload by merging backend values over the static fallback.
 * Each field is validated independently: a missing or malformed field keeps its
 * baked-in fallback rather than discarding the whole payload.
 */
function applyConfig(payload: ProductIdConfigPayload): void {
  const next = buildStaticRuntime();

  if (isPlainRecord(payload.qlm_to_stripe)) {
    next.qlmToStripe = { ...next.qlmToStripe, ...(payload.qlm_to_stripe as Record<string, string>) };
  }
  if (isPlainRecord(payload.stripe_to_qlm)) {
    next.stripeToQlm = {
      ...next.stripeToQlm,
      ...toNumberMap(payload.stripe_to_qlm as Record<string, string | number>),
    };
  }
  if (isPlainRecord(payload.tsl_features_to_stripe)) {
    next.tslFeaturesToStripe = {
      ...next.tslFeaturesToStripe,
      ...(payload.tsl_features_to_stripe as Record<string, string>),
    };
  }
  if (Array.isArray(payload.tsl_paid_products)) {
    next.tslPaidProducts = new Set<string>([
      ...next.tslPaidProducts,
      ...payload.tsl_paid_products.filter((p): p is string => typeof p === "string"),
    ]);
  }
  if (typeof payload.tsl_product_id === "string" && payload.tsl_product_id) {
    next.tslProductId = payload.tsl_product_id;
  }
  if (typeof payload.tsl_trial_product_id === "string" && payload.tsl_trial_product_id) {
    next.tslTrialProductId = payload.tsl_trial_product_id;
  }

  runtime = next;
  loaded = true;
}

/**
 * Load the canonical product-id configuration from the backend once and merge it
 * over the baked-in constants. Non-blocking and safe to call multiple times: the
 * first call performs the fetch and subsequent calls share its promise.
 *
 * On any failure (network, malformed payload, or incompatible version) the
 * baked-in constants stay in effect and the promise still resolves — callers
 * never need to await this for correctness.
 */
export function loadProductIdConfig(): Promise<void> {
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    try {
      const response = await api.get<ProductIdConfigPayload>("/config/product-ids/");
      const payload = response.data;

      if (!payload || typeof payload !== "object") {
        logger.warn("Product-id config: empty payload; keeping baked-in constants");
        return;
      }
      if (payload.version !== SUPPORTED_PRODUCT_ID_CONFIG_VERSION) {
        logger.warn(
          `Product-id config: unsupported version ${payload.version} ` +
            `(expected ${SUPPORTED_PRODUCT_ID_CONFIG_VERSION}); keeping baked-in constants`,
        );
        return;
      }

      applyConfig(payload);
      logger.debug("Product-id config loaded from backend", {
        qlmToStripe: Object.keys(runtime.qlmToStripe).length,
        stripeToQlm: Object.keys(runtime.stripeToQlm).length,
        tslFeatures: Object.keys(runtime.tslFeaturesToStripe).length,
        tslPaidProducts: runtime.tslPaidProducts.size,
      });
    } catch (error) {
      // Fallback is already in effect; this is non-fatal by design.
      logger.warn("Product-id config: load failed; keeping baked-in constants", error);
    } finally {
      // Allow an explicit retry if the first attempt failed.
      if (!loaded) {
        loadPromise = null;
      }
    }
  })();

  return loadPromise;
}

/**
 * Restore the baked-in static fallback. Intended for unit tests so each test
 * starts from a known state; not used by application code.
 */
export function __resetProductIdConfigForTests(): void {
  runtime = buildStaticRuntime();
  loadPromise = null;
  loaded = false;
}
